import { Location } from 'history';
import Immutable from 'immutable';
import $ from 'jquery';
import React from 'react';
import { Link } from 'react-router-dom';
import _ from 'underscore';

import Validation, { Form } from 'modules/react-validation';

import HttpWarning from 'sharedreact/components/HttpWarning';
import Page from 'sharedreact/components/Page';
import PageContent from 'sharedreact/components/PageContent';
import Spinner from 'sharedreact/components/Spinner';
import TextField from 'sharedreact/components/TextField';
import Authentication from 'sharedreact/data/Authentication';
import AuthenticationMethod from 'sharedreact/data/AuthenticationMethod';
import { safeIdEncode } from 'sharedreact/utils/Encode';
import { translate } from 'sharedreact/utils/LanguageSupport';
import LocationState from 'sharedreact/utils/LocationState';

function _hasType (authenticationMethods: Immutable.List<AuthenticationMethod>, type: string): boolean {
  return authenticationMethods.findIndex((value) => value.type === type) !== -1;
}

type Props = {
  authenticationMethods: Immutable.List<AuthenticationMethod>,
  language: string,
  docUrl?: string,
  location: Location<LocationState>,
  oktaLoginCode: string,
  idpName: string,
  oktaLoginError: string,
  oktaLoginErrorDetail: string,
  referrer: string,
  hasError: boolean,
  showForgotPassword: boolean,
  showResetPassword: boolean,
  forgotPasswordMailHasBeenSent: boolean,

  onLogin: (authentication: Authentication) => void,
  onLogout: () => void,
  onForgotPassword: (authentication: Authentication) => void,
  onResetPassword: (authentication: Authentication, updateState: () => void) => void,
  onLoad: (loginCode: string, idpName: string, loginError: string, loginErrorDetail: string, referrer: string) => void,
  onGoBack?: () => void
  removeOktaParametersFromPath: () => void,
};

type State = {
  authentication: Authentication,
  passwordConfirm: string,
  passwordHasBeenReset: boolean
};

class AuthenticationPage extends React.Component<Props, State> {
  private form: Form | null;

  constructor (props: Props) {
    super(props);

    this.form = null;

    this.state = {
      authentication: new Authentication(),
      passwordConfirm: '',
      passwordHasBeenReset: false
    };

    this.handleChangeValue = this.handleChangeValue.bind(this);
    this.handleChangePasswordConfirm = this.handleChangePasswordConfirm.bind(this);
    this.handleValidate = this.handleValidate.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleTextInputKeyDown = this.handleTextInputKeyDown.bind(this);
    this.processSubmit = this.processSubmit.bind(this);
  }

  override componentDidMount (): void {
    const { location, oktaLoginCode, idpName, oktaLoginError, oktaLoginErrorDetail, referrer, onLogout, onLoad } = this.props;

    if (location.state != null && location.state.logout) {
      onLogout();
    } else {
      onLoad(oktaLoginCode, idpName, oktaLoginError, oktaLoginErrorDetail, referrer);
    }

    // Force submit on Enter to work on IE and Edge
    $('form.esko_loginwindow').keydown(this.handleTextInputKeyDown);
  }

  updateState () {
    // Reset the mail sent state when returning to the login window
    if (!this.props.showForgotPassword && this.props.forgotPasswordMailHasBeenSent) {
      if (this.props.onGoBack != null) {
        const onGoBack : () => void = this.props.onGoBack;
        setTimeout(onGoBack);
      }
    }

    // Reset the password reset state when returning to the login window
    if (!this.props.showResetPassword && this.state.passwordHasBeenReset) {
      this.setState({'passwordHasBeenReset': false});
    }
  }

  override componentDidUpdate (): void {
    if (this.props.hasError) {
      this.props.removeOktaParametersFromPath();
    }

    this.updateState();
  }

  handleChangePasswordConfirm (value: string) {
    this.setState({'passwordConfirm': value});
  }

  handleChangeValue (name: string, value: string): void {
    if (name === 'userName' || name === 'password') {
      this.setState((prevState: State) => {
        return {authentication: prevState.authentication.set(name, value)};
      });
    }
  }

  handleSubmit (event: Event): boolean {
    event.preventDefault();

    this.processSubmit();

    return false;
  }

  handleTextInputKeyDown (event: JQuery.KeyDownEvent<HTMLElement>): void {
    if (event.key === 'Enter') {
      event.preventDefault();

      this.processSubmit();
    }
  }

  handleValidate (name: string) {
    this.form!.validate(name);
  }

  processSubmit (): void {
    const { onLogin, onForgotPassword, onResetPassword, showForgotPassword, showResetPassword } = this.props;

    if (showForgotPassword) {
      onForgotPassword(this.state.authentication);
    } else if (showResetPassword) {
      this.form!.validateAll();
      if (Object.keys(this.form!.getErrors()).length === 0) {
        onResetPassword(this.state.authentication, () => this.setState({'passwordHasBeenReset': true}));
      }
    } else {
      onLogin(this.state.authentication);
    }
  }

  override componentWillUnmount (): void {
    $('form.esko_loginwindow').off('keydown', this.handleTextInputKeyDown);
  }

  _renderUserNamePassword () {
    const { authentication } = this.state;

    return (
      <div key='auth_userpassword'>
        <TextField id='ae_input_login_username'
          name='userName'
          label={translate('shared.authentication.username')}
          value={authentication.userName} onChangeValue={this.handleChangeValue}
          autoComplete='username'
          selected
        />
        <TextField id='ae_input_login_password'
          name='password'
          type='password'
          label={translate('shared.authentication.password')}
          value={authentication.password} onChangeValue={this.handleChangeValue}
          autoComplete='current-password'
        />
        <div className='esko_forgotpassword'>
          <Link id='ae_button_authentication_forgotpassword' to='/login?action=forgotpassword'>{translate('shared.authentication.forgotpassword')}</Link>
        </div>
        <button id='ae_button_login_login'
          className='esko_btn esko_btn-primary esko_loginbutton'
          type='submit'>
          {translate('shared.authentication.signin')}
        </button>
      </div>
    );
  }

  _renderSSOLinks (authenticationMethods: Immutable.List<AuthenticationMethod>) {
    const location = this.props.location;

    const landingUrl: string = window.location.href;
    let referrer: string = '';
    if (location.state != null && location.state.referrer != null) {
      referrer = JSON.stringify(location.state.referrer);
    }
    let ssoMethods: Immutable.List<AuthenticationMethod> = Immutable.List();
    ssoMethods = authenticationMethods.filter((value) => value.type === AuthenticationMethod.Type.OKTA_LINK || value.type === AuthenticationMethod.Type.AZURE);

    let returnComponents = [];
    for (let i = 0; i < Math.min(3, ssoMethods.size); i++) {
      const method = ssoMethods.get(i);
      const id: string = _.uniqueId();
      const stateJson = { 'id': id, 'landingUrl': landingUrl, 'referrer': referrer, idpName: method?.name };
      const encodedState: string = safeIdEncode(JSON.stringify(stateJson));
      const fullLink = method?.link?.replace('empty_state', encodedState);
      const methodName = safeIdEncode(method?.name ?? '');
      returnComponents.push(
        <div key={'auth_okta_' + methodName }>
          <a href={fullLink}>
            <button id ={'ae_button_login_okta_' + methodName}
              className='esko_btn esko_btn-primary esko_oktaloginbutton'
              type='button'
              disabled={method?.link == null}>
              {translate('shared.authentication.signinwith', method?.name) }
            </button>
          </a>
        </div>
      )
    }

    return returnComponents;
  }

  _renderLoginForm () {
    const { authenticationMethods } = this.props;

    const hasUserPassword: boolean = _hasType(authenticationMethods, AuthenticationMethod.Type.USER_PASSWORD);
    const hasSSOLinks: boolean = _hasType(authenticationMethods, AuthenticationMethod.Type.OKTA_LINK) || _hasType(authenticationMethods, AuthenticationMethod.Type.AZURE);

    return (
      <div>
        {this._renderSSOLinks(authenticationMethods) }
        {hasSSOLinks && hasUserPassword ? <hr className='esko_loginwindowseparator esko_oktaseparator' /> : null}
        {hasUserPassword ? this._renderUserNamePassword() : null}
        <hr className='esko_loginwindowseparator' />
        <p className='esko_logintextsmall'>{translate('shared.authentication.noaccountyet')}</p>
      </div>
    );
  }

  _renderForgotPasswordForm () {
    const { forgotPasswordMailHasBeenSent, onGoBack } = this.props;
    const { authentication } = this.state;

    if (forgotPasswordMailHasBeenSent) {
      return (
        <div>
          <div key='auth_mailsent' className='esko_mailsenttext'>
            {translate('shared.authentication.resetpasswordmailsent')}
          </div>
          {onGoBack != null
            ? <hr className='esko_loginwindowseparator' />
            : null}
          {onGoBack != null
            ? <p className='esko_logintextsmall'>
              <Link id='ae_button_authentication_login' to='/login' onClick={onGoBack}>{translate('shared.authentication.gobacktologin')}</Link>
            </p>
            : null}
        </div>
      );
    }

    return (
      <div>
        <div key='auth_userpassword'>
          <div className='esko_forgotpasswordinfo'>{translate('shared.authentication.usernameoremail.info')}</div>
          <TextField id='ae_input_forgotpassword_username_or_email'
            name='userName'
            label={translate('shared.authentication.usernameoremail')}
            value={authentication.userName} onChangeValue={this.handleChangeValue}
            autoComplete='usernameOrEmail'
            selected />
          <button id='ae_button_reset_password'
            className='esko_btn esko_btn-primary esko_forgotpasswordbutton'
            type='submit'>
            {translate('shared.authentication.resetpassword')}
          </button>
        </div>
        <hr className='esko_loginwindowseparator' />
        <p className='esko_logintextsmall'>
          <span>{translate('shared.authentication.alreadyhaveanaccount')}</span>&nbsp;
          <Link id='ae_button_authentication_login' to='/login'>{translate('shared.authentication.signinsentence')}</Link>
        </p>
      </div>
    );
  }

  _renderResetPasswordForm () {
    const { authentication, passwordConfirm, passwordHasBeenReset } = this.state;

    if (passwordHasBeenReset) {
      return (
        <div key='auth_passwordreset' className='esko_mailsenttext'>
          {translate('shared.authentication.passwordhasbeenreset')}
        </div>
      );
    }

    return (
      <div>
        <div key='auth_userpassword'>
          <div style={{display: 'none'}}> { /* hidden username field to please the browser */ }
            <TextField id='ae_input_resetpassword_username'
              name='userName'
              label=''
              value={authentication.userName}
              onChangeValue={this.handleChangeValue}
              autoComplete='userName'
            />
          </div>
          <TextField id='ae_input_resetpassword_password'
            name='password'
            label={translate('shared.authentication.newpassword')}
            value={authentication.password}
            type='password'
            autoComplete='new-password'
            validations={['required']}
            onChangeValue={(name: string, value: string, event?: React.ChangeEvent<HTMLInputElement>) => this.handleChangeValue('password', value)}
            validate={() => this.handleValidate('password')}
            validationTooltipPlacement='bottom'
            selected
          />
          <TextField id='ae_input_resetpassword_password_confirm'
            name='passwordConfirm'
            label={translate('shared.authentication.newpasswordconfirm')}
            value={passwordConfirm}
            type='password'
            autoComplete='new-password'
            validations={['required', 'password']}
            onChangeValue={(name: string, value: string) => this.handleChangePasswordConfirm(value)}
            validate={() => this.handleValidate('passwordConfirm')}
            validationTooltipPlacement='bottom'
          />
          <button id='ae_button_reset_password'
            className='esko_btn esko_btn-primary esko_forgotpasswordbutton'
            type='submit'>
            {translate('shared.authentication.resetpassword')}
          </button>
        </div>
      </div>
    );
  }

  override render (): React.ReactNode {
    const { language, docUrl, oktaLoginCode, hasError, showForgotPassword, showResetPassword } = this.props;
    const showHttpWarning: boolean =  window.location.protocol === 'http:';

    // Show spinner when processing successful okta login request
    if (oktaLoginCode && !hasError) {
      return <Spinner />;
    }

    // Show either the login or forgot password form
    return (
      <Page>
        <PageContent noPadding horizontal>
          <div className='esko_infoviewparams'>
            <Validation.components.Form
              className='esko_loginwindow'
              onSubmit={this.handleSubmit}
              ref={c => { this.form = c; }}>
              <div className='esko_title'>
                <h1>
                  {translate(
                    showForgotPassword
                    ? 'shared.authentication.forgotpasswordtitle'
                    : showResetPassword
                      ? 'shared.authentication.resetpasswordtitle'
                      : 'shared.authentication.title')}
                </h1>
              </div>
              {
                showForgotPassword
                ? this._renderForgotPasswordForm()
                : showResetPassword
                  ? this._renderResetPasswordForm()
                  : this._renderLoginForm()
              }
              {showHttpWarning
                ? <div>
                  <hr className='esko_loginwindowseparator' />
                  <HttpWarning id='ae_link_gotoknowledgebase' language={language} kbUrl={docUrl} draftKBURL={false} />
                </div>
                : null
              }
            </Validation.components.Form>
          </div>
        </PageContent>
      </Page>
    );
  }
}

export default AuthenticationPage;
